// ./src/components/generic/MediaSelector/MediaSelector.tsx
//
// A drawer for choosing a media item — from the account's library, a fresh
// upload (with optional crop), or an external URL — driven by an `intent` that
// decides the media kind, storage purpose, crop aspect and whether a URL result
// is allowed. Returns either an uploaded_file_id (+ its url) or a plain url.
//
// Tabs: Library, Upload, the brand's Gallery (its ready-made pictures — offered only when the brand has some), the
// stock sources, the music library, External URL.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tabs } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { buildFullName } from "@/utils/nameHelpers";
import { UploadedFile } from "@/utils/api/UploadedFileService";
import { useApp } from "@/contexts/AppContext";
import { useFormSnapshot } from "@/hooks";
import { useUploadedFiles } from "@/hooks/useUploadedFiles";
import { WHOLE_SET } from "@/utils/api/listAllPages";

import { AppDrawer } from "../AppDrawer";
import { AppIcon } from "../AppIcon";

import { LibraryTab } from "./tabs/LibraryTab";
import { GalleryTab } from "./tabs/GalleryTab";
import { UploadTab } from "./tabs/UploadTab";
import { ExternalUrlTab } from "./tabs/ExternalUrlTab";
import { StockMediaTab } from "./tabs/StockMediaTab";
import { AudioLibraryTab } from "./tabs/AudioLibraryTab";
import { AudioLibraryItem } from "@/utils/api/AudioLibraryService";
import { StockMediaItem, StockShape, StockSource, shapeForCrop, trackStockSelect } from "./stockMedia";
import {
  MEDIA_INTENTS,
  MediaIntent,
  MediaSelectorResult,
  MediaSelectorTab,
  MediaSelectorValue,
  getMediaTabs,
} from "./mediaSelectorConfig";

export interface MediaSelectorProps {
  opened: boolean;
  onClose: () => void;
  /** What the caller is choosing media for. */
  intent: MediaIntent;
  /** The current value, to preselect when the drawer opens. */
  value?: MediaSelectorValue | null;
  /** Called with the chosen media on confirm (or per-pick in `multiple` mode). */
  onSelect: (result: MediaSelectorResult) => void;
  /**
   * Keep the drawer open and emit each pick immediately (via onSelect) so the
   * caller can accumulate several items — the footer becomes a "Done" button.
   */
  multiple?: boolean;
  /**
   * Owning-context path to tag uploads with. Build it with the helpers in
   * EventFileService (`eventContextPath` / `eventPageContextPath`) — an ad-hoc string
   * that doesn't match what the server queries by means the file uploads fine and then
   * never shows up under the event again.
   */
  contextPath?: string | null;
  /** Override the intent default for allowing a URL result / the URL tab. */
  allowUrl?: boolean;
  /** Custom drawer title. */
  title?: string;
  /** Max upload size in bytes. */
  maxFileSize?: number;
  /**
   * The event being edited — offers the free music & sounds library for audio intents (searched through our API for
   * this event's white label). Without it the library tab is not shown.
   */
  eventId?: string | null;
}

const TAB_META: Record<MediaSelectorTab, { icon: string; labelKey: string; label: string }> = {
  library: { icon: "FolderOpen", labelKey: "mediaSelector.tabLibrary", label: "Your files" },
  upload: { icon: "Upload", labelKey: "mediaSelector.tabUpload", label: "Upload" },
  gallery: { icon: "Photo", labelKey: "mediaSelector.tabGallery", label: "Gallery" },
  unsplash: { icon: "Photo", labelKey: "mediaSelector.tabUnsplash", label: "Unsplash" },
  giphy: { icon: "Movie", labelKey: "mediaSelector.tabGiphy", label: "GIPHY" },
  pexels: { icon: "Video", labelKey: "mediaSelector.tabPexels", label: "Pexels" },
  "audio-library": { icon: "Music", labelKey: "mediaSelector.tabAudioLibrary", label: "Free music & sounds" },
  url: { icon: "Link", labelKey: "mediaSelector.tabUrl", label: "URL" },
};

const STOCK_SOURCES: StockSource[] = ["unsplash", "giphy", "pexels"];

export const MediaSelector: React.FC<MediaSelectorProps> = ({
  opened,
  onClose,
  intent,
  value,
  onSelect,
  multiple = false,
  contextPath,
  allowUrl = true,
  title,
  maxFileSize,
  eventId = null,
}) => {
  const { t } = useTranslation(["common"]);
  const { user } = useApp();

  const config = MEDIA_INTENTS[intent];
  // The tabs the drawer OPENS on — fixed per intent, so the open effect below never re-runs (and drops a pick) when the
  // gallery answers a moment later.
  const baseTabs = useMemo(() => getMediaTabs(config, allowUrl, !!eventId, false), [config, allowUrl, eventId]);

  // The brand's ready-made pictures for this kind and purpose (ms_storage STANDARD files) — read WHOLE on each open: a
  // picker's options, and the answer to "is there a gallery to offer at all". A failed read still offers the tab, so
  // the failure is shown rather than the gallery silently missing.
  const {
    uploadedFiles: galleryFiles,
    isLoaded: galleryLoaded,
    lastError: galleryError,
    list: listGallery,
  } = useUploadedFiles({ autoLoad: false, pageSize: WHOLE_SET });

  useEffect(() => {
    if (!opened) {
      return;
    }

    void listGallery(
      { standard: true, kind: [config.kind], purpose: [config.purpose] },
      { page: 1, pageSize: WHOLE_SET },
      [
        { key: "filePath", direction: "asc" },
        { key: "orderNo", direction: "asc" },
      ],
      true
    );
  }, [opened, config.kind, config.purpose, listGallery]);

  const hasGallery = galleryLoaded && (galleryFiles.length > 0 || !!galleryError);
  const tabs = useMemo(
    () => getMediaTabs(config, allowUrl, !!eventId, hasGallery),
    [config, allowUrl, eventId, hasGallery]
  );

  const [activeTab, setActiveTab] = useState<MediaSelectorTab>(baseTabs[0]);
  const [pending, setPending] = useState<MediaSelectorResult | null>(null);
  const [extraFiles, setExtraFiles] = useState<UploadedFile[]>([]);
  // `multiple` mode: the accumulated multi-selection (confirmed together).
  const [selected, setSelected] = useState<MediaSelectorResult[]>([]);
  // Shared across all stock tabs so the search carries over when switching.
  const [searchQuery, setSearchQuery] = useState("");
  // #95 — the picture shape asked of the stock services, shared like the search. It opens on the shape the use wants
  // (a 16:9 banner → wide, an avatar → square), and again whenever the picker is reused for another use.
  const [stockShape, setStockShape] = useState<StockShape>(() => shapeForCrop(config.cropAspect));

  useEffect(() => {
    setStockShape(shapeForCrop(config.cropAspect));
  }, [config.cropAspect]);

  const keyOf = (r: MediaSelectorResult): string => r.uploadedFileId || r.url;

  // A pick toggles membership of the multi-selection (multiple mode) or becomes
  // the single confirmable pending selection.
  const choose = useCallback(
    (result: MediaSelectorResult | null) => {
      if (multiple) {
        if (!result) return;
        setSelected((prev) => {
          const k = keyOf(result);
          return prev.some((r) => keyOf(r) === k) ? prev.filter((r) => keyOf(r) !== k) : [...prev, result];
        });
        return;
      }
      setPending(result);
    },
    [multiple]
  );

  // Fresh uploads always join the selection (never toggle off) in multiple mode.
  const addSelected = useCallback((result: MediaSelectorResult) => {
    setSelected((prev) => (prev.some((r) => keyOf(r) === keyOf(result)) ? prev : [...prev, result]));
  }, []);

  const selectedFileIds = useMemo(() => selected.map((r) => r.uploadedFileId).filter(Boolean) as string[], [selected]);
  const selectedUrls = useMemo(() => selected.map((r) => r.url), [selected]);

  // The URL tab's text box — held here so a URL still being typed (not yet checked) counts as work (#124), and so it
  // survives a switch to another tab and back. Opens on the current value when that value is a link.
  const openedUrl = value?.url && !value?.uploadedFileId ? value.url : "";
  // What the current value IS — the open effect keys on it, not on `value`: six callers pass a new object on every
  // render, so keying on the object reset the picks, the search and the typed URL whenever the page behind re-rendered.
  const valueKey = `${value?.uploadedFileId ?? ""}|${value?.url ?? ""}`;
  const [urlText, setUrlText] = useState("");

  // Esc / a click outside / Cancel ask before dropping a pick (#123). The current value surfacing as the pending pick
  // (the library preselect, the URL tab re-validating it) is not a change, so only a DIFFERENT pick counts.
  const openedKey = (value?.uploadedFileId || value?.url || "").trim();
  const { isDirty, seedFrom, clear } = useFormSnapshot({
    picks: selected.map(keyOf),
    pending: pending && keyOf(pending) !== openedKey ? keyOf(pending) : null,
    url: urlText.trim(),
  });

  // (Re)initialise each time the drawer opens: land on the tab matching the
  // current value, and clear any prior pending selection / uploads.
  useEffect(() => {
    if (!opened) {
      clear();
      // The picker stays mounted between opens: what was typed or on screen last time must not be the first thing the
      // next open mounts (the URL tab would check that old text and make it the pick).
      setUrlText("");
      setActiveTab(baseTabs[0]);

      return;
    }

    setPending(null);
    setExtraFiles([]);
    setSearchQuery("");
    setSelected([]);
    setUrlText(openedUrl);
    seedFrom({ picks: [], pending: null, url: openedUrl.trim() });

    const hasUrlTab = baseTabs.includes("url");
    if (value?.url && !value?.uploadedFileId && hasUrlTab) {
      setActiveTab("url");
    } else {
      setActiveTab(baseTabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, valueKey, baseTabs, clear, seedFrom]);

  // The current value IS a gallery picture: open on the Gallery, with it picked — not on "Your files", where it is not.
  // Once per open, and only while the host is still where the drawer opened them.
  const galleryLandedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!opened) {
      galleryLandedFor.current = null;

      return;
    }

    const currentId = value?.uploadedFileId ?? null;
    const file = currentId ? galleryFiles.find((f) => f.id === currentId) : null;

    if (!file || !file.fileUrl || galleryLandedFor.current === currentId) {
      return;
    }

    galleryLandedFor.current = currentId;
    setActiveTab((prev) => (prev === baseTabs[0] ? "gallery" : prev));
    setPending((prev) =>
      prev && prev.uploadedFileId !== currentId
        ? prev
        : { uploadedFileId: file.id, url: file.fileUrl!, kind: config.kind, source: "gallery" }
    );
  }, [opened, value?.uploadedFileId, galleryFiles, baseTabs, config.kind]);

  const uploader = useMemo(
    () => ({ kind: "user", id: user?.id ?? null, name: user ? buildFullName(user) : null }),
    [user]
  );

  const handleLibraryPick = useCallback(
    (file: UploadedFile | null) => {
      if (!file || !file.id || !file.fileUrl) {
        if (!multiple) setPending(null);
        return;
      }

      choose({ uploadedFileId: file.id, url: file.fileUrl, kind: config.kind, source: "library" });
    },
    [config.kind, choose, multiple]
  );

  const handleGalleryPick = useCallback(
    (file: UploadedFile | null) => {
      if (!file || !file.id || !file.fileUrl) {
        if (!multiple) {
          setPending(null);
        }

        return;
      }

      choose({ uploadedFileId: file.id, url: file.fileUrl, kind: config.kind, source: "gallery" });
    },
    [config.kind, choose, multiple]
  );

  // Surface the already-set file as the initial (confirmable) pick, but never
  // override an explicit user action.
  const handlePreselectResolved = useCallback(
    (file: UploadedFile) => {
      if (!file.id || !file.fileUrl) {
        return;
      }

      setPending((prev) =>
        prev ? prev : { uploadedFileId: file.id, url: file.fileUrl!, kind: config.kind, source: "library" }
      );
    },
    [config.kind]
  );

  const handleUploaded = useCallback(
    (file: UploadedFile) => {
      if (!file.id || !file.fileUrl) {
        return;
      }

      setExtraFiles((prev) => [file, ...prev.filter((f) => f.id !== file.id)]);
      const result: MediaSelectorResult = { uploadedFileId: file.id, url: file.fileUrl, kind: config.kind, source: "upload" };
      if (multiple) {
        addSelected(result);
      } else {
        setPending(result);
      }
      setActiveTab("library");
    },
    [config.kind, multiple, addSelected]
  );

  const handleUrlPick = useCallback(
    (url: string | null) => {
      // In multiple mode, emit only on an explicit confirm (handled below), not on
      // every validation tick — so route a valid url through pending either way.
      setPending(url ? { url, kind: config.kind, source: "url" } : null);
    },
    [config.kind]
  );

  const handleStockPick = useCallback(
    (item: StockMediaItem | null) => {
      if (!item) {
        if (!multiple) setPending(null);
        return;
      }

      // Fire the provider's attribution / analytics hook, then take the URL.
      trackStockSelect(item);
      choose({ url: item.fullUrl, kind: config.kind, source: item.source });
    },
    [config.kind, choose, multiple]
  );

  // A library track: its URL, plus the credit its licence requires (the caller stores and shows it).
  const handleAudioLibraryPick = useCallback(
    (item: AudioLibraryItem | null) => {
      if (!item) {
        if (!multiple) {
          setPending(null);
        }

        return;
      }

      choose({
        url: item.url,
        kind: config.kind,
        source: "audio-library",
        credit: { title: item.title, text: item.credit, url: item.pageUrl ?? null, licenseUrl: item.licenseUrl ?? null },
      });
    },
    [config.kind, choose, multiple]
  );

  const handleConfirm = useCallback(() => {
    if (multiple) {
      // Emit each accumulated pick (plus a valid pending URL), then close.
      const all = pending ? [...selected, pending] : selected;
      all.forEach((r) => onSelect(r));
      onClose();
      return;
    }
    if (!pending) {
      return;
    }
    onSelect(pending);
    onClose();
  }, [multiple, selected, pending, onSelect, onClose]);

  const drawerTitle = title ?? t("common:mediaSelector.title", { defaultValue: "Select media" });

  const totalPicked = multiple ? selected.length + (pending ? 1 : 0) : 0;
  const confirmLabel = multiple
    ? t("common:mediaSelector.addCount", { defaultValue: "Add ({{count}})", count: totalPicked })
    : t("common:mediaSelector.select", { defaultValue: "Select" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      size="xl"
      actionType="confirm-cancel"
      onConfirm={handleConfirm}
      confirmLabel={confirmLabel}
      confirmDisabled={multiple ? totalPicked === 0 : !pending}
      cancelLabel={t("common:buttons.cancel", { defaultValue: "Cancel" })}
      isDirty={isDirty}
    >
      <Tabs value={activeTab} onChange={(v) => v && setActiveTab(v as MediaSelectorTab)} keepMounted={false}>
        <Tabs.List mb="md">
          {tabs.map((tab) => (
            <Tabs.Tab key={tab} value={tab} leftSection={<AppIcon iconName={TAB_META[tab].icon} size={16} />}>
              {t(`common:${TAB_META[tab].labelKey}`, { defaultValue: TAB_META[tab].label })}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="library">
          <LibraryTab
            kind={config.kind}
            opened={opened && activeTab === "library"}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            selectedFileId={pending?.uploadedFileId ?? null}
            multiSelect={multiple}
            selectedFileIds={selectedFileIds}
            preselectId={value?.uploadedFileId ?? null}
            extraFiles={extraFiles}
            onPick={handleLibraryPick}
            onPreselectResolved={handlePreselectResolved}
          />
        </Tabs.Panel>

        {tabs.includes("gallery") && (
          <Tabs.Panel value="gallery">
            <GalleryTab
              files={galleryFiles}
              isLoaded={galleryLoaded}
              error={galleryError}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              selectedFileId={pending?.uploadedFileId ?? null}
              multiSelect={multiple}
              selectedFileIds={selectedFileIds}
              onPick={handleGalleryPick}
            />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="upload">
          <UploadTab
            kind={config.kind}
            accept={config.accept}
            purpose={config.purpose}
            cropAspect={config.cropAspect}
            contextPath={contextPath}
            uploader={uploader}
            maxFileSize={maxFileSize}
            onUploaded={handleUploaded}
          />
        </Tabs.Panel>

        {STOCK_SOURCES.filter((s) => tabs.includes(s)).map((s) => (
          <Tabs.Panel key={s} value={s}>
            <StockMediaTab
              source={s}
              kind={config.kind}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              shape={stockShape}
              onShapeChange={setStockShape}
              selectedUrl={pending && pending.source === s ? pending.url : null}
              multiSelect={multiple}
              selectedUrls={selectedUrls}
              onPick={handleStockPick}
            />
          </Tabs.Panel>
        ))}

        {tabs.includes("audio-library") && eventId && (
          <Tabs.Panel value="audio-library">
            <AudioLibraryTab
              eventId={eventId}
              defaultKind={intent === "background-audio" ? "sounds" : "music"}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              selectedUrl={pending && pending.source === "audio-library" ? pending.url : null}
              onPick={handleAudioLibraryPick}
            />
          </Tabs.Panel>
        )}

        {tabs.includes("url") && (
          <Tabs.Panel value="url">
            <ExternalUrlTab
              kind={config.kind}
              url={urlText}
              onUrlChange={setUrlText}
              checkOnMount={!pending || pending.source === "url"}
              onPick={handleUrlPick}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </AppDrawer>
  );
};
